if (!customElements.get('nightglow-announcement-bar')) {
  class NightglowAnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.rotationSpeed = Number(this.dataset.rotationSpeed) || 4000;
      this.currentIndex = 0;
      this.timer = null;
      this.isPaused = false;
      this.clone = null;

      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.ng-announcement-bar__viewport');
      this.track = this.querySelector('.ng-announcement-bar__track');
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.track.addEventListener('transitionend', this.handleTransitionEnd);
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.addListener(this.handleMediaChange);
      }

      this.updateMode();
    }

    disconnectedCallback() {
      this.stopAutoplay();

      if (this.track) {
        this.track.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.resume);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.removeListener(this.handleMediaChange);
      }
    }

    handleMediaChange() {
      this.updateMode();
    }

    updateMode() {
      this.stopAutoplay();
      this.removeClone();
      this.currentIndex = 0;
      this.track.classList.remove('is-animating');
      this.track.style.transform = '';

      if (!this.mobileQuery.matches) {
        this.slides.forEach((slide) => {
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        });
        return;
      }

      this.updateMobileAccessibility();

      if (this.slides.length > 1) {
        this.createClone();
        this.startAutoplay();
      }
    }

    createClone() {
      if (this.clone || this.slides.length < 2) return;

      this.clone = this.slides[0].cloneNode(true);
      this.clone.dataset.announcementClone = 'true';
      this.clone.removeAttribute('data-announcement-slide');
      this.clone.removeAttribute('data-original-index');
      this.clone.removeAttribute('id');
      this.clone.removeAttribute('data-shopify-editor-block');
      this.clone.setAttribute('aria-hidden', 'true');
      this.clone.inert = true;

      this.clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      this.clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });

      this.track.appendChild(this.clone);
    }

    removeClone() {
      if (!this.clone) return;
      this.clone.remove();
      this.clone = null;
    }

    startAutoplay() {
      if (!this.mobileQuery.matches || this.slides.length < 2 || this.timer || this.isPaused || document.hidden) return;

      this.timer = window.setInterval(() => {
        this.moveNext();
      }, this.rotationSpeed);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    moveNext() {
      if (!this.mobileQuery.matches || this.slides.length < 2) return;

      this.currentIndex += 1;
      this.track.classList.add('is-animating');
      this.setTrackPosition(this.currentIndex);
      this.updateMobileAccessibility();

      if (this.reducedMotionQuery.matches && this.currentIndex >= this.slides.length) {
        this.resetToFirstSlide();
      }
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') return;

      if (this.currentIndex >= this.slides.length) {
        this.resetToFirstSlide();
      }
    }

    resetToFirstSlide() {
      this.track.classList.remove('is-animating');
      this.currentIndex = 0;
      this.setTrackPosition(0);
      this.updateMobileAccessibility();

      // Force the browser to commit the non-animated reset before the next slide.
      void this.track.offsetHeight;
    }

    setTrackPosition(index) {
      const slideHeight = this.viewport ? this.viewport.clientHeight : 40;
      this.track.style.transform = `translate3d(0, -${index * slideHeight}px, 0)`;
    }

    updateMobileAccessibility() {
      if (!this.mobileQuery.matches) return;

      const visibleIndex = this.currentIndex >= this.slides.length ? 0 : this.currentIndex;

      this.slides.forEach((slide, index) => {
        const isVisible = index === visibleIndex;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        slide.inert = !isVisible;
      });
    }

    pause() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    resume() {
      this.isPaused = false;
      this.startAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }
  }

  customElements.define('nightglow-announcement-bar', NightglowAnnouncementBar);
}
